"use client";

import { ArrowRight, Asterisk } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const team = [{
  name: "JAKUB PIOTR INDYK",
  role: "FOUNDER & DIRECTOR OF INFINIX SOLUTIONS",
  bio: "Jakub Indyk is the Founder and Director of Infinix Solutions, bringing practical experience from a Big Four professional environment and a strong understanding of accounting, financial processes, data management, and business operations.\n\n\nThroughout his professional career, Jakub has consistently looked for better ways to complete repetitive, time-consuming tasks. His ability to understand complex processes, identify inefficiencies, and develop practical automation solutions became the foundation for Infinix Solutions.\n\n\nHe combines financial and business knowledge with a strong interest in technology, data automation, and process improvement. Rather than accepting inefficient workflows as unavoidable, he focuses on finding structured, reliable, and user-friendly solutions that reduce manual work, improve accuracy, and help businesses operate more effectively.\n\n\nJakub is personally involved in understanding each client’s needs and ensuring that every solution addresses a genuine business challenge. His approach is based on professionalism, confidentiality, attention to detail, continuous improvement, and a commitment to delivering solutions that create measurable value.\n\n\nThrough Infinix Solutions, his goal is to make advanced automation, data analysis, and professional reporting accessible to businesses of all sizes—helping them save time, reduce errors, strengthen internal processes, and make decisions with greater confidence.",
  image: "https://www.infinix-solutions.net/wp-content/uploads/2026/06/profile-picture-.jpg?v=6n8lo2o04hc",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705424/img/consultant_taylor_avatar.png",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705424/img/consultant_taylor_avatar.png"
}];
export default function ValuesAndTeam() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="1848" id="values-team" className="text-foreground relative overflow-hidden py-28 dark bg-accent !h-[1200px] md:py-40">
      <svg aria-hidden="true" viewBox="0 0 720 720" className="pointer-events-none absolute -top-20 -right-20 h-[360px] w-[360px] opacity-[0.14] md:h-[720px] md:w-[720px]" fill="none">
        <g stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-foreground">
          <path d="M360 60 C 380 220, 420 360, 560 480" />
          <path d="M360 60 C 320 200, 260 320, 120 440" />
          <path d="M360 60 L 360 540" />
          {Array.from({
          length: 14
        }).map((_, i) => {
          const t = (i + 1) / 15;
          const y = 60 + t * 480;
          const spread = 40 + t * 220;
          return <g key={i} data-index={i}>
                <path d={`M360 ${y} Q ${360 - spread / 2} ${y + 20}, ${360 - spread} ${y + 60}`} />
                <path d={`M360 ${y} Q ${360 + spread / 2} ${y + 20}, ${360 + spread} ${y + 60}`} />
              </g>;
        })}
        </g>
      </svg>

      <div className="relative mx-auto max-w-[1360px] px-6 md:px-10 lg:px-16">
        <div ref={headerRef} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-foreground/80">
              <span>FOUNDER / INFINIX SOLUTIONS</span>
              <Asterisk className="size-3.5 text-primary" strokeWidth={2.5} />
              
            </div>

            <h2 className="mt-6 font-default text-[clamp(2.25rem,4.8vw,4.25rem)] font-bold leading-[1.02] tracking-tight text-foreground">One founder, one clear<div>vision for better business.</div></h2>
          </div>

          <div className="lg:col-span-5 lg:pt-4">
            <p className="font-default text-[clamp(1.0625rem,1.15vw,1.25rem)] leading-[1.55] !italic text-primary-foreground">“I created Infinix Solutions because businesses should not lose valuable time struggling with repetitive processes and unclear information. Technology should make work simpler, reporting stronger, and decisions more confident.”</p>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-7">
          {team.map((member, i) => <Card key={member.name} data-index={i} className="group flex flex-col gap-5 rounded-lg p-5 text-card-foreground shadow-md transition-all duration-[280ms] ease-out w-full bg-accent !border-none border-1 border-[rgb(0,0,0)] !h-[600px] md:!w-[1200px] hover:-translate-y-1 hover:border-primary">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {i === 0 && <img src={member.image} data-wvc-srcset={member.srcset} data-wvc-sizes={member.sizes} alt={member.name} className="shrink-0 rounded-lg object-cover !w-[200px] !h-[200px]" loading="lazy" />}
                <div className="flex flex-col gap-3 pt-1">
                  <Badge className="w-fit inline-flex items-center justify-center border whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent [a&]:hover:bg-primary/90 self-start rounded-md bg-accent px-2.5 py-1 font-mono font-medium uppercase tracking-[0.12em] text-accent-foreground !w-[310px] !text-[10px] dark:aria-invalid:ring-destructive/40">
                    {member.role}
                  </Badge>
                  <h3 className="text-[clamp(1.5rem,2vw,1.875rem)] font-bold leading-[1.15] tracking-tight !font-serif text-secondary">
                    {member.name}
                  </h3>
                  <p className="font-default leading-[1.55] text-[clamp(0.9375rem,2vw,1.25rem)] text-primary-foreground !text-justify w-full lg:!w-[860px]">
                    {member.bio}
                  </p>
                </div>
              </div>

              {i === 0 && <div className="flex justify-end mt-4">
                  
                </div>}
            </Card>)}
        </div>
      </div>
    </section>;
}